import React, { useState, useRef } from 'react';
import axios from 'axios';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Checkbox, TextField, MenuItem,
  Chip, CircularProgress, Alert, Tooltip, Divider,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import TableChartIcon from '@mui/icons-material/TableChart';
import SearchIcon from '@mui/icons-material/Search';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

const ANIOS = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);
const MESES = [
  { v: 1, l: 'Enero' }, { v: 2, l: 'Febrero' }, { v: 3, l: 'Marzo' },
  { v: 4, l: 'Abril' }, { v: 5, l: 'Mayo' }, { v: 6, l: 'Junio' },
  { v: 7, l: 'Julio' }, { v: 8, l: 'Agosto' }, { v: 9, l: 'Septiembre' },
  { v: 10, l: 'Octubre' }, { v: 11, l: 'Noviembre' }, { v: 12, l: 'Diciembre' },
];
const TIPOS_CC = [
  { value: '00', label: '00 — No Domiciliados informado' },
  { value: '01', label: '01 — Acepta propuesta SUNAT' },
  { value: '02', label: '02 — Reemplaza propuesta' },
  { value: '03', label: '03 — Ajustes posteriores al período' },
  { value: '04', label: '04 — Ajustes períodos anteriores — Formato General' },
  { value: '05', label: '05 — Ajustes períodos anteriores — Formato Simplificado' },
  { value: '06', label: '06 — No Domiciliados de períodos anteriores' },
];
const TIPO_MAP = {
  '01': 'Factura', '02': 'RHE', '03': 'Boleta', '04': 'Liq. Compra',
  '07': 'NC', '08': 'ND', '14': 'Rec. SSPP', '18': 'Doc. Banc.', '23': 'Póliza', '50': 'DUA',
};
const ORIGEN_FILTROS = [
  { v: 'TODOS', l: 'Todos' },
  { v: 'DIFIERE', l: 'Difieren' },
  { v: 'SOLO_SUNAT', l: 'Solo SUNAT' },
  { v: 'SOLO_ERP', l: 'Solo ERP' },
  { v: 'AMBOS', l: 'Coinciden' },
];

function fmtMon(v) {
  return Number(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtFecha(d) {
  if (!d) return '';
  const s = String(d).substring(0, 10);
  const [y, m, dd] = s.split('-');
  if (!y || !m || !dd) return s;
  return `${dd}/${m}/${y}`;
}

function clave(tipo, serie, numero) {
  return `${tipo}|${(serie || '').toUpperCase()}|${String(numero || '').replace(/^0+/, '') || '0'}`;
}

function compararListas(erpList, sunatList) {
  const mapErp   = new Map();
  const mapSunat = new Map();
  for (const e of erpList)   mapErp.set(clave(e.tipo_comprobante_proveedor, e.serie, e.numero), e);
  for (const s of sunatList) mapSunat.set(clave(s.tipoCdp, s.serie, s.numero), s);

  const claves = new Set([...mapErp.keys(), ...mapSunat.keys()]);
  const result = [];
  for (const k of claves) {
    const e = mapErp.get(k);
    const s = mapSunat.get(k);
    let origen;
    if (e && s) {
      const diff = Math.abs(Number(e.total || 0) - Number(s.total || 0));
      origen = diff > 0.05 ? 'DIFIERE' : 'AMBOS';
    } else if (e) {
      origen = 'SOLO_ERP';
    } else {
      origen = 'SOLO_SUNAT';
    }
    result.push({ erp: e || null, sunat: s || null, origen });
  }
  const ord = { DIFIERE: 0, SOLO_SUNAT: 1, SOLO_ERP: 2, AMBOS: 3 };
  result.sort((a, b) => (ord[a.origen] ?? 9) - (ord[b.origen] ?? 9));
  return result;
}

function chipOrigen(o) {
  if (o === 'AMBOS')      return <Chip label="COINCIDE"  color="success" size="small" sx={{ fontWeight: 'bold' }} />;
  if (o === 'DIFIERE')    return <Chip label="DIFIERE"   color="error"   size="small" sx={{ fontWeight: 'bold' }} />;
  if (o === 'SOLO_ERP')   return <Chip label="SOLO ERP"  color="info"    size="small" sx={{ fontWeight: 'bold' }} />;
  if (o === 'SOLO_SUNAT') return <Chip label="SOLO SUNAT" sx={{ bgcolor: '#ff9800', color: '#fff', fontWeight: 'bold' }} size="small" />;
  return null;
}

export default function SireComprasDeclaracion() {
  const now = new Date();
  const [anio,      setAnio]      = useState(now.getFullYear());
  const [mes,       setMes]       = useState(now.getMonth() + 1);
  const [tipoCC,    setTipoCC]    = useState('02');
  const [corr,      setCorr]      = useState('01');
  const [erpRows,   setErpRows]   = useState([]);
  const [sunatRows, setSunatRows] = useState([]);
  const [rows,      setRows]      = useState([]);
  const [selected,  setSelected]  = useState(new Set());
  const [filtroOrigen, setFiltroOrigen] = useState('TODOS');
  const [loadingErp,   setLoadingErp]   = useState(false);
  const [loadingSunat, setLoadingSunat] = useState(false);
  const [loadingTxt,   setLoadingTxt]   = useState(false);
  const [generando,    setGenerando]    = useState(false);
  const [error,        setError]        = useState('');
  const fileRef = useRef(null);

  const esAjuste = ['03', '04', '05', '06'].includes(tipoCC);

  const actualizar = (erp, sunat) => {
    const comparacion = compararListas(erp, sunat);
    setRows(comparacion);
    const presel = new Set(comparacion.filter(r => r.erp?.id).map(r => r.erp.id));
    setSelected(presel);
  };

  const handleCargarErp = async () => {
    setLoadingErp(true); setError('');
    try {
      const res = await axios.get(`/api/sire/compras?anio=${anio}&mes=${mes}`);
      const erp = res.data || [];
      setErpRows(erp);
      actualizar(erp, sunatRows);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al cargar datos ERP');
    } finally { setLoadingErp(false); }
  };

  const handleTraerSunat = async () => {
    setLoadingSunat(true); setError('');
    try {
      const res = await axios.get(`/api/sire/compras/propuesta-sunat?anio=${anio}&mes=${mes}`);
      const sunat = res.data || [];
      setSunatRows(sunat);
      actualizar(erpRows, sunat);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al obtener propuesta SUNAT');
    } finally { setLoadingSunat(false); }
  };

  const handleSubirTxt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setLoadingTxt(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post('/api/sire/compras/parsear-txt', fd);
      const sunat = res.data || [];
      setSunatRows(sunat);
      actualizar(erpRows, sunat);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al parsear TXT');
    } finally { setLoadingTxt(false); }
  };

  const filasFiltradas = filtroOrigen === 'TODOS' ? rows : rows.filter(r => r.origen === filtroOrigen);
  const idsErpSeleccionados = [...selected];

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const ids = filasFiltradas.filter(r => r.erp?.id).map(r => r.erp.id);
      setSelected(prev => { const n = new Set(prev); ids.forEach(id => n.add(id)); return n; });
    } else {
      const ids = new Set(filasFiltradas.filter(r => r.erp?.id).map(r => r.erp.id));
      setSelected(prev => { const n = new Set(prev); ids.forEach(id => n.delete(id)); return n; });
    }
  };

  const handleSelectRow = (id) => {
    setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  const handleDescargar = async (formato) => {
    if (idsErpSeleccionados.length === 0) return;
    setGenerando(true);
    try {
      const idsParam  = idsErpSeleccionados.join(',');
      const corrParam = esAjuste ? String(corr).padStart(2, '0') : '00';
      // Solo se usa para el Excel — pinta la columna "Comparativo".
      let origenParam = '';
      if (formato === 'excel') {
        const origenPorClave = {};
        rows.forEach(r => {
          const t = r.erp?.tipo_comprobante_proveedor || r.sunat?.tipoCdp;
          const s = r.erp?.serie || r.sunat?.serie;
          const n = r.erp?.numero || r.sunat?.numero;
          if (t) origenPorClave[clave(t, s, n)] = r.origen;
        });
        origenParam = `&origenPorClave=${encodeURIComponent(JSON.stringify(origenPorClave))}`;
      }
      const url = `/api/sire/compras/descargar?anio=${anio}&mes=${mes}&tipo=${tipoCC}&formato=${formato}&ids=${idsParam}&corr=${corrParam}${origenParam}`;
      const res = await axios.get(url, { responseType: 'blob' });
      const cd  = res.headers['content-disposition'] || '';
      const fn  = (cd.match(/filename="([^"]+)"/) || [])[1] || `SIRE_RCIE_${anio}${String(mes).padStart(2,'0')}.${formato}`;
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = blobUrl; a.download = fn; a.click();
      URL.revokeObjectURL(blobUrl);
    } catch { setError('Error al generar el archivo'); }
    finally { setGenerando(false); }
  };

  const cntAmbos    = rows.filter(r => r.origen === 'AMBOS').length;
  const cntDifiere  = rows.filter(r => r.origen === 'DIFIERE').length;
  const cntSoloErp  = rows.filter(r => r.origen === 'SOLO_ERP').length;
  const cntSolSunat = rows.filter(r => r.origen === 'SOLO_SUNAT').length;

  const selFiltradas = filasFiltradas.filter(r => r.erp?.id && selected.has(r.erp.id));
  const allFilSel    = filasFiltradas.filter(r => r.erp?.id).length > 0 &&
                       filasFiltradas.filter(r => r.erp?.id).every(r => selected.has(r.erp.id));
  const someFilSel   = filasFiltradas.some(r => r.erp?.id && selected.has(r.erp.id)) && !allFilSel;

  const totSelBase = selFiltradas.reduce((s, r) => s + Number(r.erp?.subtotal || 0), 0);
  const totSelIgv  = selFiltradas.reduce((s, r) => s + Number(r.erp?.total_igv || 0), 0);
  const totSelTot  = selFiltradas.reduce((s, r) => s + Number(r.erp?.total || 0), 0);

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <ShoppingCartIcon color="warning" />
        <Typography variant="h6" fontWeight="bold">SIRE — Compras Declaración (RCIE)</Typography>
        <Chip label="Comparativo ERP vs SUNAT" size="small" color="warning" variant="outlined" />
      </Box>

      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
          <TextField select label="Año" value={anio} onChange={e => setAnio(Number(e.target.value))}
            size="small" sx={{ minWidth: 100 }}>
            {ANIOS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </TextField>
          <TextField select label="Mes" value={mes} onChange={e => setMes(Number(e.target.value))}
            size="small" sx={{ minWidth: 130 }}>
            {MESES.map(m => <MenuItem key={m.v} value={m.v}>{m.l}</MenuItem>)}
          </TextField>

          <Button variant="contained" color="warning" startIcon={<SearchIcon />}
            onClick={handleCargarErp} disabled={loadingErp} sx={{ height: 40 }}>
            {loadingErp ? <CircularProgress size={18} color="inherit" /> : 'Cargar ERP'}
          </Button>

          <Divider orientation="vertical" flexItem />

          <Tooltip title="Obtiene la propuesta SUNAT usando las credenciales configuradas en la empresa">
            <Button variant="outlined" color="warning" startIcon={<CloudDownloadIcon />}
              onClick={handleTraerSunat} disabled={loadingSunat} sx={{ height: 40 }}>
              {loadingSunat ? <CircularProgress size={18} color="inherit" /> : 'Propuesta SUNAT (API)'}
            </Button>
          </Tooltip>

          <Tooltip title="Sube el TXT descargado del portal SOL de SUNAT">
            <Button variant="outlined" color="secondary" startIcon={<UploadFileIcon />}
              onClick={() => fileRef.current?.click()} disabled={loadingTxt} sx={{ height: 40 }}>
              {loadingTxt ? <CircularProgress size={18} color="inherit" /> : 'Subir TXT SUNAT'}
            </Button>
          </Tooltip>
          <input ref={fileRef} type="file" accept=".txt" hidden onChange={handleSubirTxt} />

          <Divider orientation="vertical" flexItem />

          <TextField select label="Tipo CC" value={tipoCC} onChange={e => setTipoCC(e.target.value)}
            size="small" sx={{ minWidth: 290 }}>
            {TIPOS_CC.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>

          {esAjuste && (
            <TextField label="Corr. ajuste (NN)" value={corr}
              onChange={e => setCorr(e.target.value.replace(/\D/g, '').slice(0, 2))}
              size="small" sx={{ minWidth: 140 }} inputProps={{ maxLength: 2 }} helperText="2 dígitos" />
          )}

          <Tooltip title={idsErpSeleccionados.length === 0 ? 'Selecciona al menos 1 fila ERP' : ''}>
            <span>
              <Button variant="outlined" color="warning" startIcon={<DownloadIcon />}
                onClick={() => handleDescargar('txt')}
                disabled={idsErpSeleccionados.length === 0 || generando} sx={{ height: 40 }}>
                {generando ? <CircularProgress size={16} /> : 'TXT'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={idsErpSeleccionados.length === 0 ? 'Selecciona al menos 1 fila ERP' : ''}>
            <span>
              <Button variant="outlined" color="success" startIcon={<TableChartIcon />}
                onClick={() => handleDescargar('excel')}
                disabled={idsErpSeleccionados.length === 0 || generando} sx={{ height: 40 }}>
                Excel
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {rows.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip label={`✅ COINCIDE: ${cntAmbos}`}      color="success" variant="outlined" />
          <Chip label={`⚠️ DIFIERE: ${cntDifiere}`}     color="error"   variant="outlined" />
          <Chip label={`🔵 SOLO ERP: ${cntSoloErp}`}    color="info"    variant="outlined" />
          <Chip label={`🟡 SOLO SUNAT: ${cntSolSunat}`} sx={{ borderColor: '#ff9800', color: '#ff9800' }} variant="outlined" />
          {idsErpSeleccionados.length > 0 && (
            <>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Typography variant="body2" color="text.secondary">
                <b>{idsErpSeleccionados.length}</b> sel · Base: <b>S/ {fmtMon(totSelBase)}</b> · IGV: <b>S/ {fmtMon(totSelIgv)}</b> · Total: <b>S/ {fmtMon(totSelTot)}</b>
              </Typography>
            </>
          )}
          <Box sx={{ ml: 'auto' }}>
            <TextField select label="Filtrar" value={filtroOrigen} onChange={e => setFiltroOrigen(e.target.value)}
              size="small" sx={{ minWidth: 140 }}>
              {ORIGEN_FILTROS.map(f => <MenuItem key={f.v} value={f.v}>{f.l}</MenuItem>)}
            </TextField>
          </Box>
        </Box>
      )}

      {filasFiltradas.length > 0 && (
        <TableContainer component={Paper} elevation={1}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox checked={allFilSel} indeterminate={someFilSel} onChange={handleSelectAll} color="warning" />
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Tipo</TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Serie</TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Número</TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Proveedor / RUC</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Total ERP</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Total SUNAT</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Diferencia</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filasFiltradas.map((r, idx) => {
                const e = r.erp;
                const s = r.sunat;
                const id = e?.id;
                const isSel = id && selected.has(id);
                const tipoCdpCode = e?.tipo_comprobante_proveedor || s?.tipoCdp || '';
                const tipoLabel = TIPO_MAP[tipoCdpCode] || tipoCdpCode || '—';
                const serie  = e?.serie  || s?.serie  || '—';
                const numero = e?.numero || s?.numero || '—';
                const fecha  = e?.fecha_emision ? fmtFecha(e.fecha_emision) : fmtFecha(s?.fecha);
                const nombre = e?.proveedor_nombre || s?.nombre || '—';
                const ruc    = e?.proveedor_num_doc || s?.rucDoc || '';
                const totE   = e ? Number(e.total || 0) : null;
                const totS   = s ? Number(s.total || 0) : null;
                const diff   = (totE !== null && totS !== null) ? Math.abs(totE - totS) : null;

                const rowBg = r.origen === 'DIFIERE'    ? 'rgba(255,23,68,0.06)'
                            : r.origen === 'SOLO_SUNAT' ? 'rgba(255,152,0,0.07)'
                            : undefined;

                return (
                  <TableRow key={idx} hover selected={!!isSel}
                    onClick={() => id && handleSelectRow(id)}
                    sx={{ cursor: id ? 'pointer' : 'default', bgcolor: rowBg,
                          opacity: r.origen === 'SOLO_SUNAT' ? 0.85 : 1 }}>
                    <TableCell padding="checkbox">
                      {id ? (
                        <Checkbox checked={!!isSel} color="warning"
                          onClick={ev => ev.stopPropagation()} onChange={() => handleSelectRow(id)} />
                      ) : <Box sx={{ width: 42 }} />}
                    </TableCell>
                    <TableCell>{chipOrigen(r.origen)}</TableCell>
                    <TableCell>
                      <Chip label={tipoLabel} size="small" variant="outlined"
                        color={tipoCdpCode === '01' ? 'warning' : 'default'} />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{serie}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{numero}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fecha}</TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" noWrap>{nombre}</Typography>
                      {ruc && <Typography variant="caption" color="text.secondary">{ruc}</Typography>}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {totE !== null ? `S/ ${fmtMon(totE)}` : <Typography color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {totS !== null ? `S/ ${fmtMon(totS)}` : <Typography color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: diff > 0.05 ? 'bold' : 'normal', color: diff > 0.05 ? 'error.main' : 'text.secondary' }}>
                      {diff !== null && diff > 0.005 ? `S/ ${fmtMon(diff)}` : diff !== null ? '—' : ''}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loadingErp && rows.length === 0 && (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
          <Typography color="text.secondary">
            Selecciona año y mes, presiona <b>Cargar ERP</b> y luego <b>Propuesta SUNAT (API)</b> o <b>Subir TXT SUNAT</b> para comparar.
          </Typography>
        </Paper>
      )}

    </Box>
  );
}
